import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Txt } from "@/components/Txt";
import { Button, StatusPill } from "@/components/kit";

const inputCls = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[42px] text-[14px]";

function AdjustRow({ item, onDone }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const save = async () => {
    const d = parseInt(delta, 10);
    if (!d) return toast.error("Enter a non-zero amount.");
    if (!reason.trim()) return toast.error("A reason is required.");
    try { await api.post(`/inventory/${item.inventory_id}/adjust`, { delta: d, reason }); toast.success("Stock adjusted"); setDelta(""); setReason(""); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not adjust"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`adjust-${item.inventory_id}`}>
      <input data-testid={`adjust-delta-${item.inventory_id}`} type="number" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="+/-" className="w-20 bg-warm-cream border border-ink/15 rounded-lg px-2 py-2 min-h-[40px] text-[14px] text-right" />
      <input data-testid={`adjust-reason-${item.inventory_id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className="flex-1 min-w-[140px] bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[40px] text-[14px]" />
      <Button data-testid={`adjust-save-${item.inventory_id}`} onClick={save} className="px-3 py-2 min-h-[40px] text-[13px]">Adjust</Button>
    </div>
  );
}

export default function MerchInventory({ me }) {
  const canEdit = me?.user?.is_super_admin || ["edit", "full"].includes(me?.permissions?.merch_inventory);
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ name: "", sku: "", variant: "", quantity: 0, low_stock_threshold: 5 });
  const load = useCallback(async () => { const r = await api.get("/inventory"); setItems(r.data.items); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!f.name.trim()) return toast.error("Name is required.");
    try { await api.post("/inventory", { ...f, quantity: parseInt(f.quantity) || 0, low_stock_threshold: parseInt(f.low_stock_threshold) || 0 }); toast.success("Item added"); setCreating(false); setF({ name: "", sku: "", variant: "", quantity: 0, low_stock_threshold: 5 }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  return (
    <div data-testid="merch-inventory-page">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h1 className="font-zilla text-3xl sm:text-4xl font-semibold"><Txt id="inventory.title" screen="Inventory">Merchandise Inventory</Txt></h1>
        {canEdit && <Button data-testid="inventory-add" className="shrink-0" onClick={() => setCreating((v) => !v)}>Add item</Button>}
      </div>
      <p className="text-[16px] text-ink/70 mb-5"><Txt id="inventory.subtitle" screen="Inventory">General merchandise stock. Adjust quantities manually — every change needs a reason and is logged.</Txt></p>

      {creating && (
        <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-4 max-w-xl grid grid-cols-2 gap-3" data-testid="inventory-create">
          <div className="col-span-2"><label className="text-[13px] font-semibold block mb-1">Name</label><input data-testid="inv-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} /></div>
          <div><label className="text-[13px] font-semibold block mb-1">SKU (optional)</label><input data-testid="inv-sku" value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} className={inputCls} /></div>
          <div><label className="text-[13px] font-semibold block mb-1">Variant / size (optional)</label><input data-testid="inv-variant" value={f.variant} onChange={(e) => setF({ ...f, variant: e.target.value })} className={inputCls} /></div>
          <div><label className="text-[13px] font-semibold block mb-1">Quantity</label><input data-testid="inv-qty" type="number" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} className={inputCls} /></div>
          <div><label className="text-[13px] font-semibold block mb-1">Low-stock at</label><input data-testid="inv-threshold" type="number" value={f.low_stock_threshold} onChange={(e) => setF({ ...f, low_stock_threshold: e.target.value })} className={inputCls} /></div>
          <div className="col-span-2"><Button variant="olive" data-testid="inv-create-save" onClick={create}>Save item</Button></div>
        </div>
      )}

      <div className="space-y-3" data-testid="inventory-list">
        {items.length === 0 && <div className="bg-cream border border-ink/15 rounded-xl p-10 text-center text-ink/55">No inventory items yet.</div>}
        {items.map((it) => (
          <div key={it.inventory_id} data-testid={`inv-row-${it.inventory_id}`} className="bg-cream border border-ink/15 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[16px] font-semibold flex items-center gap-2">{it.name}{it.variant && <span className="text-[12px] text-ink/50">· {it.variant}</span>}{it.low_stock && <StatusPill tone="maroon" className="text-[11px]" data-testid={`low-stock-${it.inventory_id}`}>Low stock</StatusPill>}</div>
                <div className="text-[13px] text-ink/55">{it.sku ? `SKU ${it.sku} · ` : ""}Threshold {it.low_stock_threshold}</div>
              </div>
              <div className="text-right"><div className="font-zilla text-2xl font-semibold" data-testid={`inv-qty-${it.inventory_id}`}>{it.quantity}</div><div className="text-[12px] text-ink/50">in stock</div></div>
            </div>
            {canEdit && <AdjustRow item={it} onDone={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}
